import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { toast } from "sonner";
import {
  Zap, Phone, Linkedin, Globe2, KeyRound, ShieldCheck,
  Sparkles, Check, CheckCircle2, Loader2, ArrowLeft, ArrowRight, Lock,
  Rocket, ChevronDown, Upload, Plus, Trash2, Puzzle, Settings as SettingsIcon, Github, Link2,
} from "lucide-react";
import { Button, inputClass } from "../components/kit";
import LocationPicker, { REGIONS } from "../components/LocationPicker";
import RolePicker from "../components/RolePicker";
import ResumeModePicker from "../components/ResumeModePicker";
import { CHROME_STORE_URL } from "../lib/extension";

// ── Autopilot first-run setup ────────────────────────────────────────────────
// Résumé-powered, "confirm-don't-collect" wizard. We autofill everything from the
// candidate's résumé, then only stop on the fields the parse couldn't fill — so the
// median user finishes in ~3 taps. While they tap through, we prepare their top-3
// applications in the background, and the finish line hands them off to the tracker
// with real drafts ready to approve. Nothing is ever submitted without approval.

const SESSION_SEEN = "vh_autopilot_setup_seen";

// Per-country work eligibility (mirrors ApplyProfile's AUTH_COUNTRIES / AUTH_STATUS).
// Only the two markets Autopilot applies in today. A candidate can add both at once.
const COUNTRIES = [["US", "United States"], ["CA", "Canada"]];
const STATUSES = [
  ["citizen", "Citizen", "I'm a citizen of this country"],
  ["permanent_resident", "Permanent resident", "PR / green card — no sponsorship needed"],
  ["work_permit", "Work permit / visa", "I hold a valid work permit or visa"],
  ["need_sponsorship", "Requires sponsorship", "I'd need the employer to sponsor me"],
];
const VD_GENDER = [["", "Prefer not to answer"], ["male", "Male"], ["female", "Female"], ["non_binary", "Non-binary"]];
const VD_RACE = [
  ["", "Prefer not to answer"], ["hispanic", "Hispanic or Latino"], ["white", "White"],
  ["black", "Black or African American"], ["asian", "Asian"],
  ["native_american", "Native American or Alaska Native"],
  ["pacific_islander", "Native Hawaiian or Pacific Islander"], ["two_or_more", "Two or more races"],
];
const VD_VETERAN = [["", "Prefer not to answer"], ["not_veteran", "Not a protected veteran"], ["veteran", "Protected veteran"]];
const VD_DISABILITY = [["", "Prefer not to answer"], ["no", "No, I don't have a disability"], ["yes", "Yes, I have a disability"]];

const CONSENT_TEXT =
  "I authorize VetraHub to prepare job applications on my behalf using my résumé and profile and — where auto-submission is available — to submit them to the employer's application system. I confirm the information is accurate and that I review each application before it is sent.";

// Which résumé-derived sections to celebrate in the "pulled from your résumé" rail.
const PULLED_LABELS = {
  full_name: "Name", phone: "Phone", linkedin_url: "LinkedIn", portfolio_url: "Website",
  preferred_locations: "Location", work_history: "Work history", education: "Education",
  years_experience: "Years of experience",
};

// The résumé parse fills `preferred_locations` directly, but older profiles (and thin parses)
// only carry a flat city/country. Fold those into the list so the picker opens pre-filled
// rather than blank — the wizard's promise is "confirm, don't collect".
// COUNTRY ONLY — never the city. The address on a résumé is where someone LIVES, which is not
// where they necessarily want to work, and a city is the thinnest search we can run (measured:
// Brampton 10 jobs, Ontario 1,477, Canada 2,705). Seeding the country gets them a full grid on
// day one and leaves the narrowing to them.
//
// `p.province` is read because a résumé that says "Brampton, ON" gives us the country as a
// province; the backend derives the same thing from the contact line (_country_from_location).
const CA_PROVINCES = new Set(REGIONS.Canada.map((r) => r.toLowerCase()));

const seedLocations = (p) => {
  if ((p.preferred_locations || []).filter(Boolean).length) return p;
  const explicit = (p.country || "").trim();
  if (explicit) return { ...p, preferred_locations: [explicit] };
  const prov = (p.province || "").trim().toLowerCase();
  if (!prov) return p;
  return { ...p, preferred_locations: [CA_PROVINCES.has(prov) ? "Canada" : "United States"] };
};

const detectCountryCode = (p) => {
  const hay = `${p.country || ""} ${(p.preferred_locations || []).join(" ")} ${p.city || ""}`.toLowerCase();
  if (/\b(usa|u\.s\.|united states|america)\b/.test(hay)) return "US";
  for (const [code, name] of COUNTRIES) {
    if (hay.includes(name.toLowerCase()) || new RegExp(`\\b${code.toLowerCase()}\\b`).test(hay)) return code;
  }
  return "CA";   // engine falls back to Canada when unset
};

/* ── Small building blocks ─────────────────────────────────────────────── */

function Toggle({ on, onChange, disabled }) {
  return (
    <button type="button" role="switch" aria-checked={on} disabled={disabled}
      onClick={() => !disabled && onChange(!on)}
      className={`relative inline-flex w-11 h-6 rounded-full transition-colors flex-shrink-0 ${
        disabled ? "opacity-50 cursor-not-allowed" : ""} ${on ? "bg-[#0025D8]" : "bg-secondary border border-border"}`}>
      <span className="absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200"
        style={{ transform: on ? "translateX(20px)" : "translateX(0)" }} />
    </button>
  );
}

const Eyebrow = ({ children }) => (
  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-[#0025D8] mb-2">{children}</div>
);
const H = ({ children }) => (
  <h1 className="font-display font-black text-2xl md:text-[28px] leading-tight text-[#0F172A]">{children}</h1>
);
const Sub = ({ children }) => <p className="text-sm text-muted-foreground mt-1.5 max-w-lg">{children}</p>;

/* ── Wizard ────────────────────────────────────────────────────────────── */


export default function AutopilotSetup() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("loading");   // loading | ready
  const [profile, setProfile] = useState(null);
  const [resumes, setResumes] = useState([]);
  const [pickedResume, setPickedResume] = useState("");
  const [pulled, setPulled] = useState([]);        // labels filled from résumé (for the rail)
  const [sourceResume, setSourceResume] = useState("");
  const [resumeLoading, setResumeLoading] = useState(false);   // autofilling from a résumé
  const [uploading, setUploading] = useState(false);           // uploading a new résumé file
  const autofilledFor = useRef("");                            // résumé id we've already autofilled from
  const fileInputRef = useRef(null);
  const [steps, setSteps] = useState([]);
  const [idx, setIdx] = useState(0);
  const [saving, setSaving] = useState(false);      // autosave indicator
  const [consentChecked, setConsentChecked] = useState(false);
  const [showEEO, setShowEEO] = useState(false);
  const [showProfile, setShowProfile] = useState(false);   // résumé details on Review
  const [reviewBeforeSubmit, setReviewBeforeSubmit] = useState(true);

  // Background prepare + finish-line state.
  const [matches, setMatches] = useState([]);
  const [batchId, setBatchId] = useState(null);
  const [batch, setBatch] = useState(null);
  const [premiumBlocked, setPremiumBlocked] = useState(false);
  const prepareStarted = useRef(false);
  const completeSent = useRef(false);
  const [launched, setLaunched] = useState(false);   // step 6 flips from summary to result

  const set = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  // ── Load: profile + résumés, then silently autofill from the best résumé ──
  useEffect(() => {
    (async () => {
      try {
        const [{ data: pd }, { data: rd }] = await Promise.all([
          api.get("/apply/profile"),
          api.get("/resume/saved").catch(() => ({ data: { resumes: [] } })),
        ]);
        if (pd.profile?.onboarded) { navigate("/apply", { replace: true }); return; }
        let prof = pd.profile || {};
        const rlist = rd.resumes || [];
        setResumes(rlist);
        setReviewBeforeSubmit(prof.review_before_submit !== false);

        const chosen = prof.default_resume_id || rlist[0]?.resume_id || "";
        setPickedResume(chosen);
        let filledLabels = [];
        if (rlist.length && chosen) {
          try {
            const { data: af } = await api.post("/apply/profile/autofill", { resume_id: chosen });
            const f = af.fields || {};
            prof = { ...prof, ...f, default_resume_id: chosen };
            setSourceResume(af.source_resume || "");
            filledLabels = Object.keys(f).filter((k) => PULLED_LABELS[k]).map((k) => PULLED_LABELS[k]);
            autofilledFor.current = chosen;
          } catch { /* no structured résumé — user reviews/edits manually */ }
        }
        // Work eligibility is US/CA only here — keep just those, else pre-detect one to confirm.
        const allowed = new Set(COUNTRIES.map(([c]) => c));
        const wa = Object.fromEntries(
          Object.entries(prof.work_authorization_by_country || {}).filter(([c]) => allowed.has(c)));
        prof.work_authorization_by_country = Object.keys(wa).length ? wa : { [detectCountryCode(prof)]: "citizen" };
        setProfile(seedLocations(prof));
        setPulled(filledLabels);

        // THREE STEPS, grouped by who supplies the answer. There are only four required inputs —
        // a résumé, job titles, work authorisation and consent — and the résumé itself answers
        // everything the old six-step flow asked in between.
        setSteps(["resume", "search", "review"]);
        setPhase("ready");
      } catch {
        toast.error("Couldn't start setup — please try again.");
        navigate("/apply", { replace: true });
      }
    })();
    // Warm the matches pool in the background so the finish line has jobs to prepare.
    api.get("/apply/ready-to-apply").then(({ data }) => setMatches(data.jobs || [])).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const stepId = steps[idx];

  // Persist the profile so far (fire-and-forget). ApplyProfileReq accepts the whole shape.
  const autosave = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      await api.put("/apply/profile", {
        ...profile,
        preferred_locations: (profile.preferred_locations || []).filter(Boolean),
        job_titles: (profile.job_titles || []).filter(Boolean),
      });
    } catch { /* non-fatal; saved again at the next step */ }
    finally { setSaving(false); }
  };

  // Autofill the profile from a chosen résumé (merges résumé-derived fields; user edits in Review).
  const applyResume = async (resumeId) => {
    if (!resumeId) return;
    setResumeLoading(true);
    try {
      const { data: af } = await api.post("/apply/profile/autofill", { resume_id: resumeId });
      const f = af.fields || {};
      setProfile((p) => seedLocations({ ...p, ...f, default_resume_id: resumeId }));
      setSourceResume(af.source_resume || "");
      setPulled(Object.keys(f).filter((k) => PULLED_LABELS[k]).map((k) => PULLED_LABELS[k]));
    } catch { /* structured parse failed — user fills Review manually */ }
    finally { autofilledFor.current = resumeId; setResumeLoading(false); }
  };

  const pickResume = async (resumeId) => {
    setPickedResume(resumeId);
    if (resumeId && autofilledFor.current !== resumeId) await applyResume(resumeId);
  };

  // Upload a new résumé file → save it → autofill from it. Reuses /resume/saved/upload.
  const uploadResume = async (file) => {
    if (!file) return;
    if (!/\.(pdf|docx?|txt|rtf)$/i.test(file.name)) { toast.error("Upload a PDF, DOCX, or TXT file."); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("name", (file.name.replace(/\.[^.]+$/, "") || "My résumé").slice(0, 60));
      fd.append("resume_file", file);
      const { data } = await api.post("/resume/saved/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResumes((r) => [data, ...r]);
      setPickedResume(data.resume_id);
      autofilledFor.current = "";        // force a fresh autofill from the new résumé
      await applyResume(data.resume_id);
      toast.success("Résumé uploaded — filled your details below.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (d?.plan_limit) toast.error("Your plan allows one saved résumé — delete one in Settings to add another.");
      else toast.error((typeof d === "string" && d) || "Couldn't read that file. Try a PDF or DOCX.");
    } finally { setUploading(false); }
  };

  // Work-history + education editors (same field shapes as the Settings Apply profile).
  const setWH = (i, k, v) => setProfile((p) => ({ ...p, work_history: (p.work_history || []).map((w, idx) => idx === i ? { ...w, [k]: v } : w) }));
  const addWH = () => setProfile((p) => ({ ...p, work_history: [...(p.work_history || []), { company: "", title: "", location: "", start_date: "", end_date: "", current: false, description: "" }] }));
  const removeWH = (i) => setProfile((p) => ({ ...p, work_history: (p.work_history || []).filter((_, idx) => idx !== i) }));
  const setED = (i, k, v) => setProfile((p) => ({ ...p, education: (p.education || []).map((e, idx) => idx === i ? { ...e, [k]: v } : e) }));
  const addED = () => setProfile((p) => ({ ...p, education: [...(p.education || []), { school: "", degree: "", field_of_study: "", start_date: "", end_date: "" }] }));
  const removeED = (i) => setProfile((p) => ({ ...p, education: (p.education || []).filter((_, idx) => idx !== i) }));

  // Per-country work authorization: a {code: status} map the candidate can add both US + CA to.
  // requires_sponsorship (legacy field) tracks whether ANY chosen country needs sponsorship.
  const commitAuth = (m) => setProfile((p) => ({
    ...p, work_authorization_by_country: m,
    requires_sponsorship: Object.values(m).includes("need_sponsorship"),
  }));
  const setAuthStatus = (code, status) =>
    commitAuth({ ...(profile.work_authorization_by_country || {}), [code]: status });
  const changeAuthCountry = (oldCode, newCode) => {
    const m = {};   // rebuild in place to preserve row order
    for (const [c, s] of Object.entries(profile.work_authorization_by_country || {})) m[c === oldCode ? newCode : c] = s;
    commitAuth(m);
  };
  const addAuthCountry = () => {
    const cur = profile.work_authorization_by_country || {};
    const next = COUNTRIES.find(([c]) => !(c in cur));
    if (next) commitAuth({ ...cur, [next[0]]: "citizen" });
  };
  const removeAuthCountry = (code) => {
    const m = { ...(profile.work_authorization_by_country || {}) };
    delete m[code];
    commitAuth(m);
  };

  // Kick off background preparation of the top-3 matches (once). Prepare needs no consent —
  // only sending does — so we start it as early as the work-auth step is behind us.
  const startPrepare = async () => {
    if (prepareStarted.current || premiumBlocked) return;
    const urls = (matches || []).slice(0, 3).map((m) => m.apply_url).filter(Boolean);
    if (!urls.length) return;
    prepareStarted.current = true;
    try {
      const { data } = await api.post("/apply/bulk-prepare", {
        urls, resume_id: pickedResume || profile?.default_resume_id || null,
      });
      setBatchId(data.batch_id);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (e?.response?.status === 403 || code === "premium_only") setPremiumBlocked(true);
      else prepareStarted.current = false;   // transient — allow a later retry
    }
  };

  // Poll the prepare batch so the finish line fills in live.
  useEffect(() => {
    if (!batchId) return;
    let alive = true;
    const tick = async () => {
      try {
        const { data } = await api.get(`/apply/bulk-prepare/${batchId}`);
        if (!alive) return;
        setBatch(data);
        if (data.status !== "done") setTimeout(tick, 1600);
      } catch { if (alive) setTimeout(tick, 2200); }
    };
    tick();
    return () => { alive = false; };
  }, [batchId]);

  // Mark onboarding complete + record the safety posture the moment they hit the finish line.
  // Also fire prepare here as a fallback, in case matches loaded after the work-auth/logins steps.
  useEffect(() => {
    if (stepId !== "review" || !launched) return;
    if (!completeSent.current) {
      completeSent.current = true;
      api.post("/apply/onboarding/complete", { review_before_submit: reviewBeforeSubmit }).catch(() => {});
    }
    startPrepare();
  }, [stepId, launched, reviewBeforeSubmit, matches]); // eslint-disable-line react-hooks/exhaustive-deps

  const canContinue = () => {
    if (stepId === "resume") return !!pickedResume && !resumeLoading && !uploading;
    // Two unrelated reasons can block this step, so `searchBlocker` names which one — a Continue
    // that is simply dead tells the candidate nothing about why.
    if (stepId === "search") return !searchBlocker();
    if (stepId === "review") return consentChecked;
    return true;
  };

  // Why "Your search" will not let you continue, or "" when it will. At least one role, because
  // the matches pool has nothing to search for without one and setup would end on an empty grid.
  const searchBlocker = () => {
    if (!(profile?.job_titles || []).filter(Boolean).length) return "Add at least one role to search for.";
    if (!Object.keys(profile?.work_authorization_by_country || {}).length) return "Add where you can work.";
    return "";
  };

  const goNext = async () => {
    if (!canContinue()) return;
    // Leaving the résumé step: make sure we've autofilled from the chosen résumé first.
    if (stepId === "resume" && pickedResume && autofilledFor.current !== pickedResume) {
      await applyResume(pickedResume);
    }

    await autosave();
    // Start background prep as we leave the question steps, so it overlaps the last two screens
    // and the first applications are ready by the time they reach Review.
    if (stepId === "search") startPrepare();
    setIdx((i) => Math.min(i + 1, steps.length - 1));
  };
  const goBack = () => setIdx((i) => Math.max(i - 1, 0));

  const skip = () => { sessionStorage.setItem(SESSION_SEEN, "1"); navigate("/apply", { replace: true }); };

  // Enter → continue (except inside a textarea, and not on the terminal finish screen).
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Enter" || e.target?.tagName === "TEXTAREA") return;
      // Skip form-heavy / upload steps where Enter shouldn't jump the wizard.
      if (stepId && !["review", "resume", "search"].includes(stepId)) { e.preventDefault(); goNext(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }); // re-bind each render so goNext sees fresh state

  if (phase === "loading" || !profile) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="flex items-center gap-2 font-mono text-sm text-muted-foreground">
          <Loader2 className="w-4 h-4 animate-spin" /> Reading your résumé…
        </div>
      </div>
    );
  }

  const progress = Math.min(100, Math.round(((idx + 1) / Math.max(steps.length, 1)) * 100));
  const vd = profile.voluntary_disclosures || {};
  const setVD = (k, v) => set("voluntary_disclosures", { ...(profile.voluntary_disclosures || {}), [k]: v });

  /* ── Step bodies ─────────────────────────────────────────────────────── */
  const Body = () => {
    switch (stepId) {
      case "resume": {
        const hasResumes = resumes.length > 0;
        return (
          <div>
            <Eyebrow>Résumé</Eyebrow>
            <H>{hasResumes ? "Choose your résumé" : "Add your résumé to begin"}</H>
            <Sub>Autopilot fills every application from your résumé. {hasResumes ? "Pick which one to use — or upload a new one." : "Upload one and everything below fills itself."}</Sub>
            <div className="mt-6 space-y-4">
              {hasResumes && (
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Use this résumé</label>
                  <select className={`${inputClass} cursor-pointer`} value={pickedResume}
                    disabled={resumeLoading || uploading}
                    onChange={(e) => pickResume(e.target.value)}>
                    {resumes.map((r) => (
                      <option key={r.resume_id} value={r.resume_id}>{r.name || r.optimized_name || "Résumé"}</option>
                    ))}
                  </select>
                  <p className="text-[11px] text-muted-foreground mt-1.5">This is also the résumé we submit with your applications.</p>
                </div>
              )}

              {/* Upload zone — drag-drop or click */}
              <div
                onClick={() => !uploading && fileInputRef.current?.click()}
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); uploadResume(e.dataTransfer.files?.[0]); }}
                className={`rounded-2xl border border-dashed p-6 text-center cursor-pointer transition-colors ${
                  uploading ? "border-[#0025D8]/40 bg-[#0025D8]/[0.03]" : "border-border hover:border-[#0025D8]/40 hover:bg-secondary/40"}`}>
                <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.txt,.rtf" className="hidden"
                  onChange={(e) => { uploadResume(e.target.files?.[0]); e.target.value = ""; }} />
                <div className="w-10 h-10 rounded-xl bg-[#0025D8]/10 flex items-center justify-center mx-auto mb-2">
                  {uploading ? <Loader2 className="w-5 h-5 text-[#0025D8] animate-spin" /> : <Upload className="w-5 h-5 text-[#0025D8]" />}
                </div>
                <p className="text-sm font-semibold text-[#0F172A]">
                  {uploading ? "Reading your résumé…" : hasResumes ? "Upload a different résumé" : "Drop your résumé here, or click to upload"}
                </p>
                <p className="text-[11px] text-muted-foreground mt-0.5">PDF, DOCX, or TXT</p>
              </div>

              {resumeLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" /> Reading {sourceResume || "your résumé"}…
                </div>
              ) : pulled.length > 0 ? (
                <div className="flex items-center gap-1.5 text-xs text-[#059669]">
                  <CheckCircle2 className="w-4 h-4" /> Filled {pulled.length} section{pulled.length !== 1 ? "s" : ""} from {sourceResume || "your résumé"} — review them next.
                </div>
              ) : null}

              {/* Asked here, on the résumé step, because it is a decision ABOUT the résumé they
                  just chose — and it is changeable any time from Autopilot settings. */}
              <div className="pt-4 border-t border-border">
                <label className="block text-xs font-semibold text-[#0F172A] mb-2">
                  How should we use it for each job?
                </label>
                <ResumeModePicker value={profile.resume_tailoring || "tailored"}
                  onChange={(v) => set("resume_tailoring", v)} note={false} />
              </div>
            </div>
          </div>
        );
      }

      case "search": {
        // THE ONLY THINGS A RÉSUMÉ CANNOT TELL US. Measured on a real parsed résumé, autofill
        // supplies 6 of the 12 profile fields — but never the job titles, never the work
        // authorisation. Those plus locations are what this step is for; everything the résumé
        // already answered is checked once, at the end, instead of in a step of its own.
        const rows = Object.entries(profile.work_authorization_by_country || {});
        const used = rows.map(([c]) => c);
        const canAdd = COUNTRIES.some(([c]) => !used.includes(c));
        return (
          <div>
            <Eyebrow>Your search</Eyebrow>
            <H>What should we look for?</H>
            <Sub>Three things your résumé can’t tell us. Everything else we already have.</Sub>

            <div className="mt-6 space-y-6">
              <RolePicker value={profile.job_titles || []} onChange={(v) => set("job_titles", v)} />
              <LocationPicker value={profile.preferred_locations || []}
                onChange={(v) => set("preferred_locations", v)} />
              <div className="pt-6 border-t border-border">
                <div className="text-xs font-semibold text-[#0F172A] mb-1">Employer logins</div>
                <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                  Some applications make you create an account. We handle that.</p>

            <div className="mt-6 rounded-2xl border border-[#0025D8]/20 bg-gradient-to-br from-[#0025D8]/[0.06] to-[#7C3AED]/[0.05] p-6">
              <div className="flex items-start gap-3.5">
                <div className="w-11 h-11 rounded-xl bg-[#0025D8] text-white flex items-center justify-center flex-shrink-0 shadow-sm">
                  <KeyRound className="w-5 h-5" />
                </div>
                <div>
                  <div className="flex flex-wrap gap-1.5 mb-2.5">
                    {["Workday", "iCIMS", "Oracle", "+ more"].map((t) => (
                      <span key={t} className="text-[11px] font-semibold bg-white border border-border rounded-md px-2 py-0.5 text-[#0F172A]">{t}</span>
                    ))}
                  </div>
                  <ul className="space-y-1.5 text-sm text-[#0F172A]">
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#059669]" /> Created under <span className="font-semibold">your email</span></li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#059669]" /> Password generated & encrypted for you</li>
                    <li className="flex items-center gap-2"><Check className="w-4 h-4 text-[#059669]" /> View any login anytime in <span className="font-semibold">Settings → Company logins</span></li>
                  </ul>
                </div>
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3 flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5" /> No password to type, ever.</p>
              </div>
            </div>
          

            <div className="mt-8 pt-6 border-t border-border">
              <div className="text-xs font-semibold text-[#0F172A]">Where you can work</div>
              <p className="text-xs text-muted-foreground mt-0.5 mb-1 leading-relaxed">
                We answer each job’s authorization questions for you.
              </p>
            <div className="mt-6 space-y-3">
              {rows.map(([code, status]) => {
                const opts = COUNTRIES.filter(([c]) => c === code || !used.includes(c));
                return (
                  <div key={code} className="grid grid-cols-1 sm:grid-cols-[1fr_1fr_auto] gap-2.5 sm:items-end rounded-xl border border-border p-3">
                    <div>
                      <label className="text-xs font-semibold text-[#0F172A] mb-1.5 flex items-center gap-1.5"><Globe2 className="w-3.5 h-3.5" /> Country</label>
                      <select className={`${inputClass} cursor-pointer`} value={code} onChange={(e) => changeAuthCountry(code, e.target.value)}>
                        {opts.map(([c, name]) => <option key={c} value={c}>{name}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Status</label>
                      <select className={`${inputClass} cursor-pointer`} value={status} onChange={(e) => setAuthStatus(code, e.target.value)}>
                        {STATUSES.map(([val, title]) => <option key={val} value={val}>{title}</option>)}
                      </select>
                    </div>
                    {rows.length > 1
                      ? <button type="button" onClick={() => removeAuthCountry(code)} title="Remove country"
                          className="text-muted-foreground hover:text-[#E11D48] p-2 justify-self-start sm:mb-1"><Trash2 className="w-4 h-4" /></button>
                      : <span className="hidden sm:block w-8" />}
                  </div>
                );
              })}
              <button type="button" onClick={addAuthCountry} disabled={!canAdd}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0025D8] hover:opacity-70 disabled:opacity-40 disabled:cursor-not-allowed">
                <Plus className="w-4 h-4" /> Add another country
              </button>
            </div>
          
            </div>
          </div>
        );
      }


      case "review":
        // Two faces: a summary of everything they entered, and — once they press Launch — the
        // result. Keeping both here is what makes the step's name true; the batch-progress screen
        // is the first moment Autopilot visibly does something for them, so it stays.
        return launched ? FinishLine() : ReviewSummary();
      default:
        return null;
    }
  };

  /* ── Review (pre-launch) ─────────────────────────────────────────────── */
  // Every row names its step, so "Edit" goes back to exactly where the answer was given rather
  // than dumping them at the start of the wizard.
  function ReviewSummary() {
    const titles = (profile.job_titles || []).filter(Boolean);
    const locs = (profile.preferred_locations || []).filter(Boolean);
    const wh = profile.work_history || [];
    const ed = profile.education || [];
    const auth = Object.entries(profile.work_authorization_by_country || {});
    const tailored = (profile.resume_tailoring || "tailored") === "tailored";
    const resumeName = resumes.find((r) => r.resume_id === pickedResume)?.name || "—";

    // WHAT THE RÉSUMÉ ALREADY ANSWERED IS CHECKED ONCE, HERE. It used to have a step of its own —
    // fourteen controls, every one pre-filled, gated on a name the résumé had already supplied, so
    // it could never actually stop anyone. Reviewing it twice was the only thing that step added.
    const rows = [
      ["resume", "Résumé", `${resumeName} · ${tailored ? "Tailored per job" : "Sent as written"}`],
      ["search", "Roles & locations",
        `${titles.length} role${titles.length === 1 ? "" : "s"} · ${locs.length} location${locs.length === 1 ? "" : "s"}`],
      ["search", "Work eligibility",
        auth.length ? auth.map(([c, v]) => `${c} ${String(v).replace(/_/g, " ")}`).join(" · ") : "—"],
    ];

    return (
      <div>
        <Eyebrow>Review &amp; launch</Eyebrow>
        <H>Everything we'll use on your applications</H>
        <Sub>Check it over. You can change any of this later in Settings.</Sub>

        <ul className="mt-6 rounded-2xl border border-border divide-y divide-border overflow-hidden">
          {rows.map(([id, label, value], i) => (
            <li key={i} className="flex items-start gap-3 px-4 py-3 bg-white">
              <span className="w-[136px] flex-shrink-0 text-xs font-semibold text-[#0F172A] pt-0.5">{label}</span>
              <span className="flex-1 min-w-0 text-[13px] text-muted-foreground break-words">{value || "—"}</span>
              <button type="button" onClick={() => setIdx(steps.indexOf(id))}
                className="text-xs font-semibold text-[#0025D8] hover:underline flex-shrink-0">Edit</button>
            </li>
          ))}

          {/* Collapsed by default: it is pre-filled and correct for most people, and a wall of
              inputs here would undo the step we just removed. */}
          <li className="bg-white">
            <button type="button" onClick={() => setShowProfile((v) => !v)}
              className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-secondary/40 transition-colors">
              <span className="w-[136px] flex-shrink-0 text-xs font-semibold text-[#0F172A] pt-0.5">
                From your résumé
              </span>
              <span className="flex-1 min-w-0 text-[13px] text-muted-foreground">
                {[profile.full_name || "—", profile.phone,
                  wh.length ? `${wh.length} role${wh.length === 1 ? "" : "s"}` : null,
                  ed.length ? `${ed.length} qualification${ed.length === 1 ? "" : "s"}` : null,
                ].filter(Boolean).join(" · ")}
              </span>
              <span className="text-xs font-semibold text-[#0025D8] flex-shrink-0">
                {showProfile ? "Done" : "Edit"}
              </span>
            </button>
            {showProfile && (
              <div className="px-4 pb-4 border-t border-border pt-4">
            <div className="mt-6 space-y-6">
              {/* Basics + contact + links */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-[#0F172A] mb-1.5">Full name</label>
                  <input className={inputClass} value={profile.full_name || ""} placeholder="Your name" onChange={(e) => set("full_name", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#0F172A] mb-1.5 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5" /> Phone</label>
                  <input className={inputClass} value={profile.phone || ""} placeholder="+1 555 000 0000" onChange={(e) => set("phone", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#0F172A] mb-1.5 flex items-center gap-1.5"><Linkedin className="w-3.5 h-3.5" /> LinkedIn</label>
                  <input className={inputClass} value={profile.linkedin_url || ""} placeholder="https://linkedin.com/in/…" onChange={(e) => set("linkedin_url", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#0F172A] mb-1.5 flex items-center gap-1.5"><Github className="w-3.5 h-3.5" /> GitHub <span className="font-normal text-muted-foreground/70">optional</span></label>
                  <input className={inputClass} value={profile.github_url || ""} placeholder="https://github.com/…" onChange={(e) => set("github_url", e.target.value)} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-[#0F172A] mb-1.5 flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Portfolio <span className="font-normal text-muted-foreground/70">optional</span></label>
                  <input className={inputClass} value={profile.portfolio_url || ""} placeholder="https://…" onChange={(e) => set("portfolio_url", e.target.value)} />
                </div>
              </div>

              {/* Work history */}
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Work history · {wh.length}</div>
                  <button type="button" onClick={addWH} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0025D8] hover:opacity-70"><Plus className="w-3.5 h-3.5" /> Add</button>
                </div>
                {wh.length === 0 ? <p className="text-sm text-muted-foreground italic">No work history yet — add a role.</p> : (
                  <div className="space-y-3">
                    {wh.map((w, i) => (
                      <div key={i} className="rounded-xl border border-border p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <input className={inputClass} value={w.title || ""} onChange={(e) => setWH(i, "title", e.target.value)} placeholder="Job title" />
                          <button type="button" onClick={() => removeWH(i)} className="text-muted-foreground hover:text-[#E11D48] p-1 flex-shrink-0" title="Remove"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input className={inputClass} value={w.company || ""} onChange={(e) => setWH(i, "company", e.target.value)} placeholder="Company" />
                          <input className={inputClass} value={w.location || ""} onChange={(e) => setWH(i, "location", e.target.value)} placeholder="Location" />
                          <input className={inputClass} value={w.start_date || ""} onChange={(e) => setWH(i, "start_date", e.target.value)} placeholder="Start (YYYY-MM)" />
                          <input className={inputClass} value={w.end_date || ""} onChange={(e) => setWH(i, "end_date", e.target.value)} placeholder="End (YYYY-MM)" disabled={w.current} />
                        </div>
                        <label className="flex items-center gap-2 text-sm text-[#0F172A]">
                          <input type="checkbox" className="accent-[#0025D8]" checked={!!w.current} onChange={(e) => setWH(i, "current", e.target.checked)} /> I currently work here
                        </label>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Education */}
              <div>
                <div className="flex items-center justify-between mb-2.5">
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">Education · {ed.length}</div>
                  <button type="button" onClick={addED} className="inline-flex items-center gap-1 text-xs font-semibold text-[#0025D8] hover:opacity-70"><Plus className="w-3.5 h-3.5" /> Add</button>
                </div>
                {ed.length === 0 ? <p className="text-sm text-muted-foreground italic">No education yet — add one.</p> : (
                  <div className="space-y-3">
                    {ed.map((e, i) => (
                      <div key={i} className="rounded-xl border border-border p-3 space-y-2">
                        <div className="flex items-center gap-2">
                          <input className={inputClass} value={e.school || ""} onChange={(ev) => setED(i, "school", ev.target.value)} placeholder="School" />
                          <button type="button" onClick={() => removeED(i)} className="text-muted-foreground hover:text-[#E11D48] p-1 flex-shrink-0" title="Remove"><Trash2 className="w-4 h-4" /></button>
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          <input className={inputClass} value={e.degree || ""} onChange={(ev) => setED(i, "degree", ev.target.value)} placeholder="Degree" />
                          <input className={inputClass} value={e.field_of_study || ""} onChange={(ev) => setED(i, "field_of_study", ev.target.value)} placeholder="Field of study" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <p className="text-xs text-muted-foreground flex items-center gap-1.5 flex-wrap">
                <SettingsIcon className="w-3.5 h-3.5" /> You can edit all of this anytime in
                <button type="button" onClick={() => navigate("/apply/settings")} className="font-semibold text-[#0025D8] hover:underline">Settings → Apply profile</button>.
              </p>
            </div>
          
              </div>
            )}
          </li>
        </ul>

        {/* How we apply + the authorisation, beside the button that acts on them. */}
        <div className="mt-6 pt-6 border-t border-border">
          <div className="text-xs font-semibold text-[#0F172A] mb-1">How we apply</div>
            <div className="mt-6 space-y-3">
              {/* Optimization — Honest only, deliberately no fabrication mode */}
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-[#0F172A]">Résumé optimization</span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#059669] bg-[#059669]/10 px-2 py-0.5 rounded-md"><Check className="w-3 h-3" /> Honest</span>
                </div>
                {/* "We reorder" described reordering ROLES, which the rewrite no longer does — it is
                    forbidden in the prompt. Kept generic so it does not go stale again. */}
                <p className="text-xs text-muted-foreground mt-1">We use the employer's own words and lead with what's relevant. Roles, dates and education never change.</p>
              </div>
              {/* Cover letter — automatic */}
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-[#0F172A]">Cover letter</span>
                  <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0025D8] bg-[#0025D8]/10 px-2 py-0.5 rounded-md"><Sparkles className="w-3 h-3" /> Automatic</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Written automatically, tailored to the role, only when a job asks for one. No setup needed.</p>
              </div>
              {/* Review before submit — the real toggle */}
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-sm font-semibold text-[#0F172A]">Review before submit</div>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {reviewBeforeSubmit
                        ? "On — we prepare each application and you approve it before it's sent."
                        : "Off — full hands-free."}
                    </p>
                  </div>
                  <Toggle on={reviewBeforeSubmit} onChange={setReviewBeforeSubmit} disabled />
                </div>
                <p className="text-[11px] text-muted-foreground mt-2 flex items-center gap-1.5">
                  <Lock className="w-3 h-3" /> Full hands-free Autopilot unlocks after your first approved application.
                </p>
              </div>
              {/* EEO — auto-declined, optional expander */}
              <div className="rounded-xl border border-border p-4">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-sm font-semibold text-[#0F172A]">Diversity questions (EEO)</span>
                  <span className="text-[11px] font-semibold text-muted-foreground bg-secondary px-2 py-0.5 rounded-md">Auto-declined</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Optional questions like gender or ethnicity. We answer "prefer not to answer" unless you choose otherwise.</p>
                <button type="button" onClick={() => setShowEEO((v) => !v)}
                  className="text-xs font-semibold text-[#0025D8] mt-2 inline-flex items-center gap-1">
                  <ChevronDown className={`w-3.5 h-3.5 transition-transform ${showEEO ? "rotate-180" : ""}`} /> {showEEO ? "Hide" : "Add my info (optional)"}
                </button>
                {showEEO && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    {[["gender", "Gender", VD_GENDER], ["race_ethnicity", "Race / ethnicity", VD_RACE],
                      ["veteran_status", "Veteran status", VD_VETERAN], ["disability_status", "Disability status", VD_DISABILITY]].map(([key, label, opts]) => (
                      <div key={key}>
                        <label className="block text-[11px] font-semibold text-[#0F172A] mb-1">{label}</label>
                        <select className={`${inputClass} text-sm py-2 cursor-pointer`} value={vd[key] || ""}
                          onChange={(e) => setVD(key, e.target.value)}>
                          {opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                        </select>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              {/* Authorization — required */}
              <label className="flex items-start gap-2.5 rounded-xl border border-[#0025D8]/25 bg-[#0025D8]/[0.03] p-4 cursor-pointer">
                <input type="checkbox" checked={consentChecked} onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 w-4 h-4 accent-[#0025D8] flex-shrink-0" />
                <span className="text-xs text-[#0F172A] leading-relaxed">{CONSENT_TEXT}</span>
              </label>
            </div>
          
        </div>
      </div>
    );
  }

  const launch = async () => {
    // Consent is given on this screen now, so it is recorded here rather than on a step that no
    // longer exists.
    try { await api.post("/apply/consent", { agreed: true }); } catch { /* re-prompted at approve */ }
    await autosave();
    setLaunched(true);
  };

  /* ── Finish line ─────────────────────────────────────────────────────── */
  // OFFERED AFTER LAUNCH, NEVER DURING SETUP. Some ATSes gate their submit behind anti-bot checks
  // that only a real browser clears — Ashby, iCIMS and SmartRecruiters, which is 12-14% of a real
  // matched pool measured 2026-10-09. Those applications park in `ext_queued` until the extension
  // can finish them.
  //
  // It sits here rather than in a step because installing it means leaving for the Chrome store,
  // and a candidate who wanders off mid-wizard may not come back. By this point their applications
  // are already being prepared, so the ask is concrete and skipping it costs them nothing today.
  function ChromeExtensionRow() {
    const live = Boolean(CHROME_STORE_URL);
    return (
      <div className="mt-8 rounded-2xl border border-border bg-secondary/30 p-4 text-left flex items-start gap-3">
        <span className="w-9 h-9 rounded-xl bg-[#0025D8]/10 grid place-items-center flex-shrink-0">
          <Puzzle className="w-4 h-4 text-[#0025D8]" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-[#0F172A]">
            Reach about 1 in 8 more of your matches
          </div>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            Some employers only accept an application from a real browser. Our extension
            handles those.
          </p>
        </div>
        {live ? (
          <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer"
             className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[#0025D8] hover:bg-[#001FB0] text-white px-3.5 py-2 text-xs font-bold transition-colors">
            <Puzzle className="w-3.5 h-3.5" /> Add to Chrome
          </a>
        ) : (
          <span title="Publishing to the Chrome Web Store — available shortly"
                className="flex-shrink-0 inline-flex items-center gap-1.5 rounded-xl bg-[#0F172A]/[0.06] text-[#0F172A]/45 px-3.5 py-2 text-xs font-bold cursor-default">
            <Puzzle className="w-3.5 h-3.5" /> Coming soon
          </span>
        )}
      </div>
    );
  }

  function FinishLine() {
    const items = (batch?.items || []).filter((it) => it.status === "ready");
    const total = batch?.total ?? Math.min((matches || []).length, 3);
    const done = (batch?.ready_count || 0) + (batch?.failed_count || 0);
    const allDone = batch?.status === "done";
    const noMatches = !premiumBlocked && !batchId && (matches || []).length === 0;

    return (
      <div className="text-center">
        <div className="w-16 h-16 rounded-2xl bg-[#059669]/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-9 h-9 text-[#059669]" />
        </div>
        <H>You're all set on Autopilot 🎉</H>

        {premiumBlocked ? (
          <>
            <Sub>Your profile is ready. Upgrade to Premium and we'll apply to your matches for you.</Sub>
            <div className="mt-6 flex flex-col items-center gap-2.5">
              <Button to="/dashboard"><Zap className="w-4 h-4" /> Upgrade to Premium</Button>
              <button onClick={() => navigate("/apply")} className="text-sm text-muted-foreground hover:text-[#0025D8]">Go to Autopilot</button>
            </div>
          </>
        ) : noMatches ? (
          <>
            <Sub>We're still finding jobs that match you — they'll appear in your Matches shortly, each ready to apply on Autopilot.</Sub>
            <div className="mt-6"><Button onClick={() => navigate("/apply")}><Rocket className="w-4 h-4" /> Go to Autopilot</Button></div>
          </>
        ) : (
          <>
            <Sub>{allDone
              ? `${items.length} application${items.length !== 1 ? "s" : ""} prepared and ready to review. Nothing is sent until you approve.`
              : "Preparing your first applications… you can review and approve them on the next screen."}</Sub>

            <div className="mt-6 space-y-2 text-left max-w-md mx-auto">
              {items.map((it) => (
                <div key={it.draft_id} className="flex items-center gap-3 rounded-xl border border-border bg-white p-3">
                  <CheckCircle2 className="w-5 h-5 text-[#059669] flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#0F172A] truncate">{it.title || "Application"}</div>
                    <div className="text-xs text-muted-foreground truncate">{it.company || ""}</div>
                  </div>
                </div>
              ))}
              {!allDone && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground px-1 pt-1">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Preparing {Math.max(done, items.length)} of {total}…
                </div>
              )}
            </div>

            <div className="mt-6 flex flex-col items-center gap-2.5">
              <Button onClick={() => navigate(`/apply/tracker?batch=${batchId || ""}`)}>
                <Rocket className="w-4 h-4" /> Review &amp; approve
              </Button>
              <button onClick={() => navigate("/apply")} className="text-sm text-muted-foreground hover:text-[#0025D8]">Skip for now — go to Autopilot</button>
            </div>
          </>
        )}

        <ChromeExtensionRow />

        <button type="button" onClick={() => navigate("/apply/settings")}
          className="mt-6 inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-[#0025D8]">
          <SettingsIcon className="w-3.5 h-3.5" /> Edit your details anytime in Settings
        </button>
      </div>
    );
  }


  // The step list, in the order the wizard walks them. Labels are what the rail shows.
  const STEP_LABELS = { resume: "Résumé", search: "Your search", review: "Review & launch" };
  const onResult = stepId === "review" && launched;     // terminal: no Back, no Continue
  const stepNo = Math.min(idx + 1, steps.length);

  return (
    <div className="min-h-screen bg-white lg:grid lg:grid-cols-[340px_1fr]">
      {/* ── Rail. A full-height dark column on desktop; the same information condensed into a
             short dark bar on a phone, where a 340px list would cost 40% of the screen and
             nothing on it is actionable anyway. ── */}
      <aside className="bg-[#0B1437] text-white lg:min-h-screen lg:flex lg:flex-col px-6 lg:px-8 py-5 lg:py-10">
        <div className="flex items-center gap-2 font-mono text-[11px] tracking-[0.12em] text-white/70">
          <Zap className="w-4 h-4 text-white" />
          <span className="font-semibold text-white">Autopilot setup</span>
        </div>

        {/* Desktop: the pitch and the whole journey. */}
        <div className="hidden lg:block mt-12 flex-1">
          <h1 className="font-display font-black text-[34px] leading-[1.12] tracking-tight">
            Set up once. Autopilot<br />fills every application.
          </h1>
          <p className="text-sm text-white/55 mt-4 leading-relaxed max-w-[260px]">
            Six short steps. Everything you answer here is reused on every form.
          </p>

          <ol className="mt-10 space-y-1">
            {steps.map((id, i) => {
              const done = i < idx, current = i === idx;
              return (
                <li key={id}>
                  <button type="button"
                    // Backwards only: jumping forward would skip a gate the next step depends on.
                    onClick={() => { if (i < idx && !onResult) setIdx(i); }}
                    disabled={i >= idx || onResult}
                    className={`w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors ${
                      current ? "bg-white/[0.08]" : done ? "hover:bg-white/[0.05] cursor-pointer" : "cursor-default"}`}>
                    <span className={`w-6 h-6 rounded-full grid place-items-center text-[11px] font-bold flex-shrink-0 ${
                      current ? "bg-[#4F6BFF] text-white"
                              : done ? "bg-white/15 text-white/90" : "border border-white/20 text-white/35"}`}>
                      {done ? <Check className="w-3 h-3" /> : i + 1}
                    </span>
                    <span className={`text-[13.5px] ${
                      current ? "text-white font-semibold" : done ? "text-white/70" : "text-white/35"}`}>
                      {STEP_LABELS[id] || id}
                    </span>
                  </button>
                </li>
              );
            })}
          </ol>
        </div>

        {/* Phone: current step + progress, in place of the list. */}
        <div className="lg:hidden mt-3 flex items-baseline justify-between gap-3">
          <span className="text-sm font-semibold">{STEP_LABELS[stepId] || ""}</span>
          <span className="font-mono text-[10px] tracking-[0.18em] text-white/50">
            STEP {stepNo} OF {steps.length}
          </span>
        </div>

        <div className="mt-3 lg:mt-0">
          <div className="hidden lg:flex items-center justify-between font-mono text-[10px] tracking-[0.18em] text-white/45 mb-2">
            <span>STEP {stepNo} OF {steps.length}</span>
            <span>{progress}%</span>
          </div>
          <div className="h-1 rounded-full bg-white/12 overflow-hidden">
            <div className="h-full bg-[#4F6BFF] transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
        </div>
      </aside>

      {/* ── Content ── */}
      <main className="min-w-0 flex flex-col">
        <div className="flex items-center justify-between gap-4 px-6 md:px-12 pt-6">
          <span className="inline-flex items-center gap-1.5 font-mono text-[11px] text-muted-foreground">
            {saving
              ? <><Loader2 className="w-3 h-3 animate-spin" /> Saving…</>
              : <><Check className="w-3.5 h-3.5 text-[#059669]" /> Autosaved</>}
          </span>
          {!onResult && (
            <button onClick={skip} className="text-xs font-semibold text-muted-foreground hover:text-[#0F172A]">
              I'll finish later
            </button>
          )}
        </div>

        <div className="flex-1 px-6 md:px-12 py-8 md:py-10">
          <div className="max-w-2xl">
            {Body()}

            {!onResult && (
              <div className="mt-10 pt-5 border-t border-border flex items-center justify-between gap-3">
                <button onClick={goBack} disabled={idx === 0}
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-[#0F172A] disabled:opacity-40 disabled:cursor-not-allowed">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
                {stepId === "review" ? (
                  <Button onClick={launch} loading={saving}>
                    <Rocket className="w-4 h-4" /> Launch Autopilot
                  </Button>
                ) : (
                  <div className="flex items-center gap-3">
                    {/* Name the reason. Two different things can disable this button, and a dead
                        Continue with no explanation is the worst of both. */}
                    {stepId === "search" && searchBlocker() && (
                      <span className="text-[11.5px] text-[#B45309] text-right max-w-[220px]">
                        {searchBlocker()}
                      </span>
                    )}
                    <Button onClick={goNext} disabled={!canContinue()} loading={resumeLoading || uploading}>
                      Continue <ArrowRight className="w-4 h-4" />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
